import {
    FormControl,
    IterateOptions
} from '../../../../components/FormUtilities'
import InputField from '../../../../components/InputField'
import SelectTouch from '../../../../components/SelectTouch'
import SelectMultiple from '../../../../components/SelectMultiple'
import DropImage from '../../../../components/DropImage'
import NumeralField from '../../../../components/NumeralField'
import Toggle from '../../../../components/Toggle'
import Upload from '../../../../components/Upload'
import { formInformation } from '../../storage'
import { useState, useEffect } from 'react'
import { Option } from '../../../../config/types/component'

interface ProductOption {
    id: number
    name: string
}

interface CountryOption extends ProductOption {
    code: string
}
function SortDescription() {
    const { props, getError } = formInformation.useInput('name')
    const helper = getError(
        'ERROR-0000107',
        'Ya existe un producto con el mismo nombre'
    )

    return (
        <FormControl label="Descripción corta" required>
            <InputField
                {...props}
                placeholder="Ingresar descripción corta"
                autoFocus
                helper={helper || props.helper}
                regex={null}
            />
        </FormControl>
    )
}

function ManufacturerCode() {
    const { props } = formInformation.useInput('manufacturer_code')
    return (
        <FormControl label="Código Fabricante" required>
            <InputField
                {...props}
                placeholder="Ingresar código fabricante"
                maxLength={255}
            />
        </FormControl>
    )
}

function Composition() {
    const { props } = formInformation.useInput('composition')
    return (
        <FormControl label="Composición">
            <InputField
                {...props}
                placeholder="Ingresar composición"
                maxLength={255}
            />
        </FormControl>
    )
}

// Helper function to find option by ID in loaded data
function findOptionById<T extends { id: number; name: string }>(
    data: T[],
    id: number | string | null | undefined
): { label: string; value: string } | null {
    if (id == null || !data || data.length === 0) return null
    const idNum = typeof id === 'string' ? parseInt(id, 10) : id
    if (isNaN(idNum)) return null
    const option = data.find(item => item.id === idNum)
    if (!option) return null
    return {
        label: option.name,
        value: idNum.toString()
    }
}

function UsefulLife() {
    const { props, data } = formInformation.useSelect<ProductOption>(
        'useful_life',
        '/v5.6/apis/product_options/useful_life'
    )

    const [currentOption, setCurrentOption] = useState<Option | null>(
        props.option || null
    )

    useEffect(() => {
        if (
            props.option?.value &&
            props.option.label === '' &&
            data.length > 0
        ) {
            const foundOption = findOptionById(data, props.option.value)
            if (foundOption) {
                setCurrentOption(foundOption)
                formInformation.setValue('useful_life', foundOption, false)
            }
        } else {
            setCurrentOption(props.option || null)
        }
    }, [props.option, data])

    function handleChange(option: Option) {
        formInformation.setValue('useful_life', option, true)
        setCurrentOption(option)
    }

    return (
        <FormControl label="Vida Útil">
            <SelectTouch
                {...props}
                option={currentOption}
                placeholder="Seleccionar vida útil"
                onChange={handleChange}
                onSearch={null}
                clear={false}
            >
                <IterateOptions data={data} label="name" />
            </SelectTouch>
        </FormControl>
    )
}

function BrandSelect() {
    const { props, data } = formInformation.useSelect<any>(
        'brand',
        '/v5.6/apis/product_options/brands'
    )

    const [currentOption, setCurrentOption] = useState<Option | null>(
        props.option || null
    )

    useEffect(() => {
        if (
            props.option?.value &&
            props.option.label === '' &&
            data.length > 0
        ) {
            const foundOption = findOptionById(data, props.option.value)
            if (foundOption) {
                setCurrentOption(foundOption)
                formInformation.setValue('brand', foundOption, false)
            }
        } else {
            setCurrentOption(props.option || null)
        }
    }, [props.option, data])

    function handleChange(option: Option) {
        formInformation.setValue('brand', option, true)
        setCurrentOption(option)
    }

    return (
        <FormControl label="Marca" required>
            <SelectTouch
                {...props}
                option={currentOption}
                placeholder="Seleccionar marca"
                onChange={handleChange}
                clear={false}
            >
                <IterateOptions data={data} label="name" />
            </SelectTouch>
        </FormControl>
    )
}

function CountryAvailability() {
    const { props, data } = formInformation.useSelects<CountryOption>(
        'country_availability',
        '/v5.6/apis/product_options/countries'
    )
    return (
        <FormControl label="Disponibilidad País">
            <SelectMultiple
                {...props}
                placeholder="Seleccionar país(es)"
                selectTouchInherit={true}
                onSearch={null}
                clear={false}
            >
                <IterateOptions data={data} label="name" />
            </SelectMultiple>
        </FormControl>
    )
}

function ImpactEcosystem() {
    const { props, data } = formInformation.useSelects<ProductOption>(
        'impact_ecosystem',
        '/v5.6/apis/product_options/impact_ecosystems'
    )
    const resolvedOptions = (props.options || []).map(opt => {
        const found = data.find(
            item => item.id.toString() === String(opt.value)
        )
        return found ? { label: found.name, value: opt.value } : opt
    })
    return (
        <FormControl label="Ecosistema de Impacto">
            <SelectMultiple
                {...props}
                options={resolvedOptions}
                placeholder="Seleccionar ecosistema(s)"
                selectTouchInherit={true}
                onSearch={null}
                clear={false}
            >
                <IterateOptions data={data} label="name" />
            </SelectMultiple>
        </FormControl>
    )
}

function ProductiveProcess() {
    const { props, data } = formInformation.useSelects<ProductOption>(
        'productive_process',
        '/v5.6/apis/product_options/productive_processes'
    )
    const resolvedOptions = (props.options || []).map(opt => {
        const found = data.find(
            item => item.id.toString() === String(opt.value)
        )
        return found ? { label: found.name, value: opt.value } : opt
    })
    return (
        <FormControl label="Proceso Productivo">
            <SelectMultiple
                {...props}
                options={resolvedOptions}
                placeholder="Seleccionar proceso(s) productivo(s)"
                selectTouchInherit={true}
                onSearch={null}
                clear={false}
            >
                <IterateOptions data={data} label="name" />
            </SelectMultiple>
        </FormControl>
    )
}

function VerticalMarket() {
    const { props, data } = formInformation.useSelects<ProductOption>(
        'vertical_market',
        '/v5.6/apis/product_options/vertical_markets'
    )
    const resolvedOptions = (props.options || []).map(opt => {
        const found = data.find(
            item => item.id.toString() === String(opt.value)
        )
        return found ? { label: found.name, value: opt.value } : opt
    })
    return (
        <FormControl label="Mercado Vertical">
            <SelectMultiple
                {...props}
                options={resolvedOptions}
                placeholder="Seleccionar mercado(s) vertical(es)"
                selectTouchInherit={true}
                onSearch={null}
                clear={false}
            >
                <IterateOptions data={data} label="name" />
            </SelectMultiple>
        </FormControl>
    )
}

function isCountrySelected(countryId: number) {
    const availability = formInformation.store().country_availability || []
    return availability.some(c => parseInt(String(c.value), 10) === countryId)
}

function FiscalCodeNI() {
    const { props } = formInformation.useInput('fiscal_code_ni')
    const enabled = isCountrySelected(1)
    return (
        <FormControl label="Código Fiscal NI">
            <InputField
                {...props}
                placeholder="Ingresar código fiscal NI"
                maxLength={255}
                disabled={!enabled}
            />
        </FormControl>
    )
}

function FiscalCodeCR() {
    const { props } = formInformation.useInput('fiscal_code_cr')
    const enabled = isCountrySelected(2)
    return (
        <FormControl label="Código Fiscal CR" required={enabled}>
            <InputField
                {...props}
                placeholder="Ingresar código fiscal CR"
                maxLength={255}
                disabled={!enabled}
            />
        </FormControl>
    )
}

function FiscalCodePA() {
    const { props } = formInformation.useInput('fiscal_code_pa')
    const enabled = isCountrySelected(3)
    return (
        <FormControl label="Código Fiscal PA" required={enabled}>
            <InputField
                {...props}
                placeholder="Ingresar código fiscal PA"
                maxLength={255}
                disabled={!enabled}
            />
        </FormControl>
    )
}

function FiscalCodeVE() {
    const { props } = formInformation.useInput('fiscal_code_ve')
    const enabled = isCountrySelected(4)
    return (
        <FormControl label="Código Fiscal VE">
            <InputField
                {...props}
                placeholder="Ingresar código fiscal VE"
                maxLength={255}
                disabled={!enabled}
            />
        </FormControl>
    )
}

function Photo() {
    const { props, getError } = formInformation.useImage('photo')
    const helper = getError()
    return (
        <FormControl label="Foto">
            <DropImage {...props} helper={helper || props.helper} />
        </FormControl>
    )
}

function Packaging() {
    const { props, data } = formInformation.useSelect<ProductOption>(
        'packaging',
        '/v5.6/apis/product_options/packaging'
    )

    const [currentOption, setCurrentOption] = useState<Option | null>(
        props.option || null
    )

    useEffect(() => {
        if (
            props.option?.value &&
            props.option.label === '' &&
            data.length > 0
        ) {
            const foundOption = findOptionById(data, props.option.value)
            if (foundOption) {
                setCurrentOption(foundOption)
                formInformation.setValue('packaging', foundOption, false)
            }
        } else {
            setCurrentOption(props.option || null)
        }
    }, [props.option, data])

    function handleChange(option: Option) {
        formInformation.setValue('packaging', option, true)
        setCurrentOption(option)
    }

    return (
        <FormControl label="Empaque">
            <SelectTouch
                {...props}
                option={currentOption}
                placeholder="Seleccionar empaque"
                onChange={handleChange}
                onSearch={null}
                clear={false}
            >
                <IterateOptions data={data} label="name" />
            </SelectTouch>
        </FormControl>
    )
}

function PresentationQuantity() {
    const { props } = formInformation.useInput('presentation_quantity')
    return (
        <FormControl label="Cantidad por Presentación" required>
            <NumeralField
                {...props}
                placeholder="Ingresar cantidad por presentación"
                max={999999}
            />
        </FormControl>
    )
}

function PresentationUnit() {
    const { props, data } = formInformation.useSelect<ProductOption>(
        'presentation_unit',
        '/v5.6/apis/product_options/measurement_units'
    )

    const [currentOption, setCurrentOption] = useState<Option | null>(
        props.option || null
    )

    useEffect(() => {
        if (
            props.option?.value &&
            props.option.label === '' &&
            data.length > 0
        ) {
            const foundOption = findOptionById(data, props.option.value)
            if (foundOption) {
                setCurrentOption(foundOption)
                formInformation.setValue(
                    'presentation_unit',
                    foundOption,
                    false
                )
            }
        } else {
            setCurrentOption(props.option || null)
        }
    }, [props.option, data])

    function handleChange(option: Option) {
        formInformation.setValue('presentation_unit', option, true)
        setCurrentOption(option)
    }

    return (
        <FormControl label="Unidad de Presentación" required>
            <SelectTouch
                {...props}
                option={currentOption}
                placeholder="Seleccionar unidad de presentación"
                onChange={handleChange}
                onSearch={null}
                clear={false}
            >
                <IterateOptions data={data} label="name" />
            </SelectTouch>
        </FormControl>
    )
}

function AirTransport() {
    const { props } = formInformation.useCheck('air_transport')
    return (
        <FormControl label="Transporte Aéreo">
            <Toggle {...props} size="md" />
        </FormControl>
    )
}

function MaritimeTransport() {
    const { props } = formInformation.useCheck('maritime_transport')
    return (
        <FormControl label="Transporte Marítimo">
            <Toggle {...props} size="md" />
        </FormControl>
    )
}

function Refrigeration() {
    const { props } = formInformation.useCheck('refrigeration')
    return (
        <FormControl label="Requiere Refrigeración">
            <Toggle {...props} size="md" />
        </FormControl>
    )
}

function Regulation() {
    const { props, data } = formInformation.useSelect<ProductOption>(
        'regulation',
        '/v5.6/apis/product_options/regulation'
    )

    const [currentOption, setCurrentOption] = useState<Option | null>(
        props.option || null
    )

    useEffect(() => {
        if (
            props.option?.value &&
            props.option.label === '' &&
            data.length > 0
        ) {
            const foundOption = findOptionById(data, props.option.value)
            if (foundOption) {
                setCurrentOption(foundOption)
                formInformation.setValue('regulation', foundOption, false)
            }
        } else {
            setCurrentOption(props.option || null)
        }
    }, [props.option, data])

    function handleChange(option: Option) {
        formInformation.setValue('regulation', option, true)
        setCurrentOption(option)
    }

    return (
        <FormControl label="Regulación">
            <SelectTouch
                {...props}
                option={currentOption}
                placeholder="Seleccionar regulación"
                onChange={handleChange}
                onSearch={null}
                clear={false}
            >
                <IterateOptions data={data} label="name" />
            </SelectTouch>
        </FormControl>
    )
}

function Certification() {
    const typeProductValue = formInformation.store().type_product?.value
    const { props, data } = formInformation.useSelects<ProductOption>(
        'certification',
        typeProductValue !== undefined
            ? `/v5.6/apis/product_options/certifications?type_product=${typeProductValue}`
            : '/v5.6/apis/product_options/certifications'
    )
    const resolvedOptions = (props.options || []).map(opt => {
        const found = data.find(
            item => item.id.toString() === String(opt.value)
        )
        return found ? { label: found.name, value: opt.value } : opt
    })

    return (
        <FormControl label="Certificación">
            <SelectMultiple
                {...props}
                options={resolvedOptions}
                placeholder="Seleccionar certificación(es)"
                selectTouchInherit={true}
                onSearch={null}
                clear={false}
            >
                <IterateOptions data={data} label="name" />
            </SelectMultiple>
        </FormControl>
    )
}

function Literature() {
    const { props } = formInformation.useUploads('literature')
    return (
        <FormControl label="Literatura" col="span-6" row="span-3">
            <Upload {...props} extensions={['pdf', 'doc', 'docx']} />
        </FormControl>
    )
}

export default function Fields() {
    return (
        <>
            <SortDescription />
            <ManufacturerCode />
            <Composition />
            <UsefulLife />
            <BrandSelect />
            <CountryAvailability />
            <ImpactEcosystem />
            <ProductiveProcess />
            <VerticalMarket />
            <FiscalCodeNI />
            <FiscalCodeCR />
            <FiscalCodePA />
            <FiscalCodeVE />
            <Photo />
            <Packaging />
            <MaritimeTransport />
            <PresentationQuantity />
            <AirTransport />
            <PresentationUnit />
            <Refrigeration />
            <Regulation />
            <Certification />
            <Literature />
        </>
    )
}
